import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import 'styles/main.css';

import API from 'files/api.js';
import moment from 'moment-timezone';
import smoothscroll from 'smoothscroll-polyfill';

import Appearance from 'styles/Appearance.js';
import Cart from 'classes/Cart.js';
import Customer from 'classes/Customer.js';
import Product from 'classes/Product.js';
import ShopifyClient from 'files/ShopifyClient.js';
import Utils from 'files/Utils.js';
import Analytics from 'files/Analytics.js';

import Header from 'structure/Header.js';
import Footer from 'structure/Footer.js';
import Hero from 'views/Hero.js';
import TrustBar from 'views/TrustBar.js';
import Stats from 'views/Stats.js';
import About from 'views/About.js';
import RiskData from 'views/RiskData.js';
import HowItWorks from 'views/HowItWorks.js';
import ComparisonChart from 'views/ComparisonChart.js';
import ProductGrid from 'views/ProductGrid.js';
import FeaturedProduct from 'views/FeaturedProduct.js';
import MobileApp from 'views/MobileApp.js';
import Guarantee from 'views/Guarantee.js';
import GRACI from 'views/GRACI.js';
import Consultation from 'views/Consultation.js';
import { ServiceabilityProvider, DealerOnly } from 'views/ServiceabilityProvider.js';
import Testimonials from 'views/Testimonials.js';
import FAQ from 'views/FAQ.js';
import ProductDetail from 'views/ProductDetail.js';
import AboutPage from 'views/pages/AboutPage.js';
import DealerProgramPage from 'views/pages/DealerProgramPage.js';
import FaqPage from 'views/pages/FaqPage.js';
import SupportPage from 'views/pages/SupportPage.js';
import ShippingPage from 'views/pages/ShippingPage.js';
import ReturnsPage from 'views/pages/ReturnsPage.js';
import ContactPage from 'views/pages/ContactPage.js';
import BuildSystemPage from 'views/pages/BuildSystemPage.js';
import QuizModal from 'views/QuizModal.js';
import OutreachCaptureModal from 'views/OutreachCaptureModal.js';
import QuizCTA from 'views/QuizCTA.js';
import CartDrawer from 'views/CartDrawer.js';
import AccountDrawer from 'views/AccountDrawer.js';

// Initialize smooth scroll polyfill
smoothscroll.polyfill();

window.theme = 'light';

const App = () => {

    const history = useHistory();
    const dealershipRef = useRef(null);
    const [cartOpen, setCartOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const [checkout, setCheckout] = useState(null);
    const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
    const [products, setProducts] = useState(Product.getAll());
    const [theme, setTheme] = useState(window.theme || 'default');
    const [customer, setCustomer] = useState(Customer.get());
    const [accountOpen, setAccountOpen] = useState(false);
    const [quizOpen, setQuizOpen] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);

    // Email-capture prompt on launch. Once someone grabs a code we never prompt
    // again; a plain dismissal only cools it off for 30 days, then re-prompts.
    useEffect(() => {
        if(typeof window === 'undefined') {
            return;
        }
        const ls = window.localStorage;
        if(ls.getItem('aql_subscribed')) {
            return; // already got a code — don't nag
        }
        const dismissedAt = parseInt(ls.getItem('aql_email_prompt_dismissed_at') || '0', 10);
        const coolingOff = 30 * 24 * 60 * 60 * 1000; // 30 days
        if(dismissedAt && (Date.now() - dismissedAt) < coolingOff) {
            return; // dismissed recently — wait out the window
        }
        const t = setTimeout(() => setEmailModalOpen(true), 1200);
        return () => clearTimeout(t);
    }, []);
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);

    const initRef = useRef(false);

    // Initialize Shopify client and cart on mount
    useEffect(() => {
        if(initRef.current) {
            return;
        }
        initRef.current = true;
        initializeStore();
    }, []);

    // Subscribe to cart updates
    useEffect(() => {
        const unsubscribe = Cart.subscribe((updatedCheckout) => {
            setCheckout(updatedCheckout);
            setCartCount(Cart.getItemCount());
        });
        return () => unsubscribe();
    }, []);

    // Subscribe to customer auth updates
    useEffect(() => {
        const unsubscribe = Customer.subscribe((updatedCustomer) => {
            setCustomer(updatedCustomer);
        });
        return () => unsubscribe();
    }, []);

    const initializeStore = async () => {
        try {

            // Restore customer session / complete a "Sign in with Shop" callback
            let signedInCustomer = await Customer.init();
            setCustomer(signedInCustomer);

            // Initialize the cart (creates or restores Shopify checkout)
            let cartCheckout = await Cart.init();
            if(cartCheckout) {
                setCheckout(cartCheckout);
                setCartCount(Cart.getItemCount());
            }

            // Fetch products from Shopify and merge with local catalog
            let shopifyProducts = await ShopifyClient.fetchAllProducts();
            if(shopifyProducts && shopifyProducts.length > 0) {
                let localProducts = Product.getAll();
                let merged = localProducts.map(local => {
                    let shopifyMatch = shopifyProducts.find(sp =>
                        sp.handle === local.handle ||
                        sp.title.toLowerCase().includes(local.shortTitle.toLowerCase())
                    );
                    return Product.mergeWithShopify(local, shopifyMatch);
                });
                setProducts(merged);
            }
        } catch(e) {
            console.log('Store initialization (Shopify may not be configured yet):', e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = useCallback(async (product, source) => {
        // Use the first variant ID if available from Shopify
        let variantId = product.variants?.[0]?.id;
        if(!variantId) {
            // If no Shopify variant, open cart to show it's a placeholder
            console.log('No Shopify variant available for:', product.title);
            setCartOpen(true);
            return;
        }
        let result = await Cart.addItem(variantId, 1);
        if(result) {
            Analytics.addToCart([{ product, quantity: 1 }], { source });
        }
        setCartOpen(true);
    }, []);

    const handleUpdateCartItem = useCallback(async (lineItemId, quantity) => {
        if(quantity <= 0) {
            await Cart.removeItem(lineItemId);
        } else {
            await Cart.updateItem(lineItemId, quantity);
        }
    }, []);

    const handleRemoveCartItem = useCallback(async (lineItemId) => {
        await Cart.removeItem(lineItemId);
    }, []);

    // Sign in (redirects to Shop) when logged out, or open the account drawer
    const handleAccount = useCallback(async () => {
        if(!Customer.isAuthenticated()) {
            try {
                await Customer.login();
            } catch(e) {
                console.error('Unable to start sign-in:', e.message);
            }
            return;
        }
        setAccountOpen(true);
        setOrdersLoading(true);
        try {
            let result = await Customer.getOrders();
            setOrders(result);
        } catch(e) {
            console.error('Unable to load orders:', e.message);
            setOrders([]);
        } finally {
            setOrdersLoading(false);
        }
    }, []);

    const handleSignOut = useCallback(async () => {
        await Customer.logout();
        setOrders([]);
        setAccountOpen(false);
    }, []);

    const handleCheckout = useCallback(() => {
        let url = Cart.getCheckoutUrl();
        if(url) {
            Analytics.beginCheckout(Cart.get());
            window.open(url, '_self');
        }
    }, []);

    // Scroll to a homepage section — first return home if we're on another route
    const scrollToSection = useCallback((section) => {
        if(window.location.pathname !== '/') {
            history.push('/');
            setTimeout(() => Utils.scrollToElement(section), 60);
        } else {
            Utils.scrollToElement(section);
        }
    }, [history]);

    const handleShopNow = useCallback(() => {
        scrollToSection('products');
    }, [scrollToSection]);

    const handleViewProduct = useCallback((product) => {
        history.push(`/products/${product.handle}`);
    }, [history]);

    // Add a quiz-recommended package: Base Network + N à-la-carte water sensors.
    const handleAddPackage = useCallback(async (extraSensors, packageKey) => {
        const starter = products.find(p => p.handle === 'starter-pack');
        const sensor = products.find(p => p.handle === 'water-sensor');
        const starterVariant = starter?.variants?.[0]?.id;
        const sensorVariant = sensor?.variants?.[0]?.id;
        try {
            const added = [];
            if(starterVariant && await Cart.addItem(starterVariant, 1)) {
                added.push({ product: starter, quantity: 1 });
            }
            if(sensorVariant && extraSensors > 0 && await Cart.addItem(sensorVariant, extraSensors)) {
                added.push({ product: sensor, quantity: extraSensors });
            }
            Analytics.addToCart(added, { source: 'quiz', packageKey });
        } catch(e) {
            console.log('Package add-to-cart error:', e.message);
        }
        if(!starterVariant) {
            console.log('No Shopify variant for starter-pack yet — set it up in Shopify to enable checkout.');
        }
        setQuizOpen(false);
        setCartOpen(true);
    }, [products]);

    // Accepts a plain section string (scroll), or { route } / { section }
    const handleNavigate = useCallback((nav) => {
        if(typeof nav === 'string') {
            scrollToSection(nav);
        } else if(nav && nav.route) {
            history.push(nav.route);
            window.scrollTo(0, 0);
        } else if(nav && nav.section) {
            scrollToSection(nav.section);
        }
    }, [scrollToSection, history]);

    const onSetStyleSheetProperties = () => {
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--theme', window.theme);
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());
        document.querySelector('meta[name="theme-color"]').setAttribute("content", Appearance.colors.background());
        setTheme(window.theme);
    }

    const utils = {
        api: {
            headers: () => {
                return {
                    'Content-Type': 'application/json',
                    'X-API': `Version ${API.version}`,
                    'X-Timezone': `TZ ${moment.tz.guess()}`,
                    'X-Web': `Build ${API.build}`
                }
            }
        },
        dealership: {
            get: () => dealershipRef.current
        },
        theme: {
            get: () => window.theme,
            set: val => {
                window.theme = val;
                onSetStyleSheetProperties();
            }
        }
    }

    return (
        <ServiceabilityProvider utils={utils}>
        <div style={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: Appearance.colors.background(),
            position: 'relative',
        }}>

            {/* Fixed Header */}
            <Header
                cartCount={cartCount}
                customer={customer}
                onAccountClick={handleAccount}
                onCartClick={() => setCartOpen(true)}
                onNavigate={handleNavigate} />

            {/* Main Content */}
            <main>
              <Switch>
                <Route path={'/products/:handle'} render={({ match }) => (
                    <ProductDetail
                        handle={match.params.handle}
                        products={products}
                        loading={loading}
                        onAddToCart={handleAddToCart} />
                )} />
                <Route path={'/about'} render={() => <AboutPage />} />
                <Route path={'/careers'} render={() => <DealerProgramPage onNavigate={handleNavigate} utils={utils} />} />
                <Route path={'/faq'} render={() => <FaqPage />} />
                <Route path={'/support'} render={() => <SupportPage />} />
                <Route path={'/shipping'} render={() => <ShippingPage />} />
                <Route path={'/returns'} render={() => <ReturnsPage />} />
                <Route path={'/contact'} render={() => <ContactPage utils={utils} />} />
                <Route path={'/build-your-system'} render={() => (
                    <BuildSystemPage products={products} onAddPackage={handleAddPackage} />
                )} />
                <Route path={'/'}>
                {/* Hero Section */}
                <Hero onShopNow={handleShopNow} onOpenQuiz={() => setQuizOpen(true)} />

                {/* Trust Bar — credibility strip for cold traffic */}
                <TrustBar />

                {/* Product Grid — pricing right under the hero, ahead of the story sections */}
                <ProductGrid
                    products={products}
                    loading={loading}
                    onAddToCart={handleAddToCart}
                    onViewDetails={handleViewProduct}
                    onOpenQuiz={() => setQuizOpen(true)} />

                {/* Trust story — heritage framing + proof-of-scale stats */}
                <Stats />

                {/* Risk data — the stakes, in cited III/ISO statistics */}
                <RiskData products={products} />

                {/* About / Mission Section */}
                <About />


                {/* How It Works — demo video + Detect / Alert / Act */}
                <HowItWorks />

                {/* Comparison — Aqua Link vs. competitor leak sensors (approved Oct 2026) */}
                <ComparisonChart />

                {/* Mobile Companion App */}
                <MobileApp />

                {/* GRACI — AI Safety Assistant */}
                <GRACI />

                {/* Testimonials */}
                <Testimonials />

                {/* Risk reversal — 14-day returns / warranty / support */}
                <Guarantee />
                
                {/* In-home consultation — only for dealer-area visitors (no online sales, dealer nearby) */}
                <DealerOnly><Consultation utils={utils} /></DealerOnly>

                {/* Build-your-system CTA — throughline */}
                <QuizCTA
                    onOpenQuiz={() => setQuizOpen(true)}
                    heading={'Ready to protect your home?'}
                    subtext={'Tell us about your home and we’ll build the exact system it needs — Base Network plus the right number of sensors.'} />

                {/* FAQ — objection handling */}
                <FAQ />

                </Route>
              </Switch>
            </main>

            {/* Footer */}
            <Footer 
            onNavigate={handleNavigate} 
            utils={utils} />

            {/* Cart Drawer */}
            <CartDrawer
                open={cartOpen}
                onClose={() => setCartOpen(false)}
                cart={checkout}
                sensorTiers={(products || []).find(p => p.handle === 'water-sensor')?.tiers || null}
                offerPercents={Object.fromEntries((products || []).map(p => [p.handle, p.offerPercent || 0]))}
                onUpdateItem={handleUpdateCartItem}
                onRemoveItem={handleRemoveCartItem}
                onCheckout={handleCheckout} />

            {/* Account Drawer (order history + tracking) */}
            <AccountDrawer
                open={accountOpen}
                onClose={() => setAccountOpen(false)}
                customer={customer}
                orders={orders}
                loading={ordersLoading}
                onSignOut={handleSignOut} />

            {/* Build-your-system quiz modal */}
            <QuizModal
                open={quizOpen}
                onClose={() => setQuizOpen(false)}
                products={products}
                onAddPackage={handleAddPackage} />

            {/* Email capture — 10% off Base Network, shown on launch */}
            <OutreachCaptureModal
                open={emailModalOpen}
                onSubscribed={() => {
                    if(typeof window !== 'undefined') { window.localStorage.setItem('aql_subscribed', '1'); }
                }}
                onClose={() => {
                    if(typeof window !== 'undefined') { window.localStorage.setItem('aql_email_prompt_dismissed_at', String(Date.now())); }
                    setEmailModalOpen(false);
                }}
                utils={utils} />
        </div>
        </ServiceabilityProvider>
    );
};

export default App;
